import { green, grey } from "@ant-design/colors"
import { faRocket, faSave } from "@fortawesome/pro-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { Button, Divider, Popconfirm, Skeleton, notification } from "antd"
import { useContext, useEffect, useState } from "react"
import { useQueryClient } from "react-query"
import { AuthenticationContext } from "../AuthenticationContext"
import { itemClassName } from "../common/constants"
import CatalogItemForm from "../components/ItemCatalog/CatalogItemForm"
import CheckingForAffectedJobsModal from "../components/ItemCatalog/CheckingForAffectedJobsModal"
import ItemCatalogSidebar from "../components/ItemCatalog/ItemCatalogSidebar"
import UpdateJobsModal from "../components/ItemCatalog/UpdateJobsModal"
import { getJobsForItem } from "../components/ItemCatalog/api"
import { useCatalogItem, useInvalidateItemCatalog, useRestoreCatalogItemMutation, useRetireCatalogItemMutation, useSaveCatalogItemMutation } from "../components/ItemCatalog/apiQueries"
import { CatalogItemFormValues, hasPricingChanged } from "../components/ItemCatalog/catalogItemModel"
import ThemableButton from "../components/Items/ThemableButton"
import HeroButton from "../components/common/HeroButton"
import HeroButtonsContainer from "../components/common/HeroButtonsContainer"
import HeroContainer from "../components/common/HeroContainer"
import VSpace from "../components/common/VSpace"
import { CatalogItem, defaultCatalogItem } from "../domain/catalogItem"
import { uniqueId } from "../helpers/uniqueId"
import { NavigationService } from "../services/navigationService"

const NEW_CATALOG_ITEM_ID = 0

export type Props = {
  navigationService: NavigationService
}

const parseIdFromHash = (hash: string): number => {
  const hashItemId = hash.split('-')[0]
  if (hashItemId === 'new') {
    return NEW_CATALOG_ITEM_ID
  } else {
    return parseInt(hashItemId)
  }
}

const ItemCatalogPage = ({ navigationService }: Props) => {
  const { currentUser } = useContext(AuthenticationContext)
  const heroImage = currentUser?.tenant.publicFolderUrl + '/images/default-item.jpg'

  const [selectedCatalogItemId, setSelectedCatalogItemId] = useState<number | undefined>(parseIdFromHash(navigationService.hash))

  const catalogItemQuery = useCatalogItem(selectedCatalogItemId)
  const [catalogItem, setCatalogItem] = useState<CatalogItem | undefined>()
  useEffect(() => {
    if (catalogItemQuery.data) {
      setCatalogItem(catalogItemQuery.data)
    }
  }, [catalogItemQuery.data])

  useEffect(() => {
    if (catalogItem) {
      navigationService.setHash(`${catalogItem.id}`)
    }
  }, [catalogItem?.id])

  const [formValue, setFormValue] = useState<CatalogItemFormValues | undefined>()
  const [saveEnabled, setSaveEnabled] = useState(false)

  const [updatingItem, setUpdatingItem] = useState<CatalogItemFormValues | undefined>()
  const [showCheckingJobsModal, setShowCheckingJobsModal] = useState(false)

  const queryClient = useQueryClient()

  const upsertCatalogItemMutation = useSaveCatalogItemMutation((catalogItem) => {
    setSelectedCatalogItemId(catalogItem.id)
    navigationService.setHash(`${catalogItem.id}`)
  })

  const retireItemMutation = useRetireCatalogItemMutation({
    onSuccess: () => {
      setSelectedCatalogItemId(undefined)
      setCatalogItem(undefined)
      navigationService.setHash('')
    }
  })

  const restoreItemMutation = useRestoreCatalogItemMutation((id: number) => {
    setSelectedCatalogItemId(undefined)
    setCatalogItem(undefined)
    navigationService.setLocation(`/items#${id}`)
  })

  const [retired, setRetired] = useState(false)
  const invalidateItemCatalog = useInvalidateItemCatalog()

  useEffect(() => {
    setRetired(navigationService.path.includes('deleted'))
    invalidateItemCatalog()
  }, [navigationService.hash])

  const confirmDiscardChanges = () => {
    if (!saveEnabled) {
      return true
    }
    return confirm('The current item has changes. Do you want to discard them?')
  }

  const handleSelect = (item: CatalogItem) => {
    if (!confirmDiscardChanges()) {
      return
    }
    setSelectedCatalogItemId(item.id)
    navigationService.setHash(`${item.id}`)
  }

  const handleChanges = (values: CatalogItemFormValues, dirty: boolean) => {
    setFormValue(values)
    setSaveEnabled(dirty)
  }

  const saveChanges = async () => {
    if (!formValue) {
      return
    }

    setSaveEnabled(false)
    if (formValue.id !== NEW_CATALOG_ITEM_ID && hasPricingChanged(catalogItem!, formValue)) {
      setShowCheckingJobsModal(true)
      const associatedJobs = await queryClient.fetchQuery(['jobs-for-item', formValue.id], () => getJobsForItem(formValue.id!))
      setShowCheckingJobsModal(false)

      if (associatedJobs.length > 0) {
        setUpdatingItem(formValue)
        return
      }
    }

    upsertCatalogItemMutation.mutate(
      { catalogItem: formValue! }, 
      { onSuccess: () => notification.success({ message: 'Successfully updated this item' }) }
    )
  }

  const handleUpdateJobs = async (action: 'ALL' | 'NONE' | 'SOME', jobIds: number[]) => {
    const updateAllJobs = action === 'NONE' ? undefined : action === 'ALL'
    upsertCatalogItemMutation.mutate({ catalogItem: updatingItem!, updateAllJobs, jobIds }, {
      onSuccess: () => {
        if (jobIds.length > 0 && action !== 'NONE') {
          notification.success({ message: `Successfully updated the pricing of this item in ${jobIds.length} jobs` })
        } else {
          notification.success({ message: 'Successfully updated this item' })
        }
        setUpdatingItem(undefined)
      }
    })
  }

  const handleCreate = () => {
    if (!confirmDiscardChanges()) {
      return
    }
    setCatalogItem({
      ...defaultCatalogItem(),
      name: 'New Item',
      guid: uniqueId(),
    })
    setSelectedCatalogItemId(NEW_CATALOG_ITEM_ID)
    navigationService.setHash(`new`)
    setSaveEnabled(true)
  }

  return <>
    <ItemCatalogSidebar onSelect={handleSelect} onCreate={handleCreate} retired={retired} navigationService={navigationService}/>
    <div id="main" className="has-elements fluid-container">
      {catalogItem && <>
        <HeroContainer imgSrc={heroImage} />
        <div className="hero-details">
          <h1>{catalogItem.name}<br /><small>{itemClassName(catalogItem.class)}</small></h1>
          {catalogItem.retiredAt && <h1 className="hero-details-deleted-item">
            <span>
              <span className="margin-30">Retired</span>
            </span>
          </h1>}
          <HeroButtonsContainer>
            <HeroButton icon={faSave}
              onClick={saveChanges}
              disabled={!saveEnabled}
            />
          </HeroButtonsContainer>
        </div>
        <section>
          <VSpace>
            {catalogItemQuery.isLoading && <Skeleton active />}
            {!catalogItemQuery.isLoading && <>
              <CatalogItemForm
                catalogItem={catalogItem}
                onChanges={handleChanges}
              />
              {catalogItem.id > 0 && <>
                <Divider />
                {!catalogItem.retiredAt &&
                  <Popconfirm onConfirm={() => retireItemMutation.mutate(catalogItem.id)} title="Are you sure you want to retire this item?">
                    <Button danger type="primary">Retire Item</Button>
                  </Popconfirm>
                }
                {catalogItem.retiredAt &&
                  <Popconfirm onConfirm={() => restoreItemMutation.mutate(catalogItem.id)} title="Are you sure you want to restore this item?">
                    <ThemableButton colorPrimary={green.primary} type="primary">Restore Item</ThemableButton>
                  </Popconfirm>
                }
              </>}
            </>}
          </VSpace>
        </section>
      </>}
      {!catalogItem &&
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '24px',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          width: '100%',
          color: grey[2],
        }}>
          <FontAwesomeIcon icon={faRocket} size="6x" />
          <h1 style={{ fontFamily: 'Proza Libre, sans-serif' }}>Select an Item</h1>
        </div>
      }

    </div>
    <UpdateJobsModal
      open={!!updatingItem}
      onCancel={() => setUpdatingItem(undefined)}
      itemId={updatingItem?.id ?? 0}
      onUpdateJobs={handleUpdateJobs}
      loading={upsertCatalogItemMutation.isLoading}
    />
    <CheckingForAffectedJobsModal open={showCheckingJobsModal} />
  </>
}

export default ItemCatalogPage
